import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { Alert } from '@winnin/winnin_ui.components.alert'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { Loading } from '@winnin/winnin_ui.components.loading'
import { Title } from '@winnin/winnin_ui.components.title'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import SortByFilterV2 from 'components/SortByFilterV2'
import { ROLES } from 'consts'
import { T, useTranslate } from 'lib/tolgee'

import { UserStore, VideoModalStore } from '../../stores'
import BlockComponents from '../BlockComponents'
import EmptyState from '../EmptyState'
import GridListSwitcher from '../GridListSwitcher'
import SeeMoreCard from '../SeeMoreCard'
import VideoCard from '../VideoCard'
import VideoCardList from '../VideoCardList'

import './index.scss'

const REMOVE_VIDEO_FROM_TOPIC = gql`
  mutation removeVideoFromTopic($postId: String!, $topicId: Int!, $provider: String!) {
    removeFromTopic(topicId: $topicId, postId: $postId, provider: $provider) {
      msg
      status
    }
  }
`

/**
 * @typedef {Object} VideosGridProps
 * @property {string} [titleTooltipText] - The tooltip text for the title.
 * @property {() => void} [customExportButtonComponent] - The custom export button component.
 * @property {() => void} loadMore - The function to load more videos.
 * @property {boolean} [hideListView] - Indicates if the list view should be hidden.
 * @property {Array} videos - The array of videos.
 * @property {string} [topicId] - The ID of the topic.
 * @property {boolean} hasMoreVideos - Indicates if there are more videos available.
 * @property {number} [videoCount] - The number of videos.
 * @property {boolean} loading - Indicates if the videos are currently loading.
 * @property {string} [gridWrapperClass] - The class name for the grid wrapper.
 * @property {() => void} [onSeeMoreCard] - The function to handle the "See More" button click.
 * @property {boolean} [seeMoreAsCard] - Indicates if the "See More" button should be rendered as a card.
 * @property {string} [customEmptyStateText] - The custom text for the empty state.
 * @property {string} [customEmptyStateHeaderText] - The custom header text for the empty state.
 * @property {boolean} shouldRenderMetaWarning - Indicates if the meta warning should be rendered.
 * @property {boolean} shouldRenderTwitchWarning - Indicates if the Twitch warning should be rendered.
 * @property {(rows) => void} [header] - The header text.
 * @property {string} [subheader] - The subheader text.
 * @property {boolean} [shouldNotBlockJob] - Indicates if the job should not be blocked.
 * @property {() => void} [onVideoClickCallback] - The function to handle video click.
 * @property {boolean} [shouldEnableCreatorClick] - Indicates if creator click should be enabled.
 * @property {() => void} [onCreatorClickCallback] - The function to handle creator click.
 * @property {boolean} [showSortBy] - Indicates if the sort by filter should be shown.
 * @property {string} pageName - The name of the page.
 * @property {boolean} [hideRowsInTitle] - Indicates if the rows should be hidden in the title.
 * @property {boolean} [isPublic] - Indicates if the videos are public.
 * @property {boolean} [hideCheckbox] - Indicates if the checkbox should be hidden.
 * @property {Array} [sortByOptions] - The sort by options.
 */

/**
 * @param {VideosGridProps} props
 */
const VideosGrid = ({
  titleTooltipText,
  customExportButtonComponent,
  loadMore,
  hideListView,
  videos,
  topicId,
  hasMoreVideos,
  videoCount,
  loading,
  gridWrapperClass = 'videos-grid-wrapper-switcher',
  onSeeMoreCard,
  seeMoreAsCard,
  customEmptyStateText,
  customEmptyStateHeaderText,
  shouldRenderMetaWarning,
  shouldRenderTwitchWarning,
  header,
  subheader,
  shouldNotBlockJob = false,
  onVideoClickCallback,
  shouldEnableCreatorClick = false,
  onCreatorClickCallback,
  showSortBy = false,
  pageName,
  hideRowsInTitle,
  isPublic = false,
  hideCheckbox = false,
  sortByOptions,
}) => {
  const t = useTranslate()

  const [observerHookRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMoreVideos,
    onLoadMore: loadMore,
  })

  const [removeVideoFromTopic] = useMutation(REMOVE_VIDEO_FROM_TOPIC, {
    onCompleted({ removeFromTopic }) {
      if (removeFromTopic.status === false) {
        alert(`Not removed because of ${removeFromTopic.msg}`)
      }
    },
    onError(error) {
      console.error('REMOVE_VIDEO_FROM_TOPIC@ removeVideoFromTopic', error)
      alert(`Not removed because of ${error}`)
    },
  })

  const [state, setState] = useState({ listView: 'grid' })

  const isAdminUser = UserStore.get('role') >= ROLES.ADMIN
  const isViewerUser = UserStore.getRole === 'viewer'

  const onClickRemoveVideo = (event, video) => {
    const { provider, postId } = video
    event.stopPropagation()
    alert(
      'This video will be reprocessed in our database and removed from the current topic, that action can take a little while'
    )

    removeVideoFromTopic({
      variables: { topicId: parseInt(topicId), provider, postId },
    })
  }

  const handleVideoClick = (currentVideo, videos, index) => {
    const formattedCurrentVideo = {
      postId: currentVideo.postId,
      provider: currentVideo.provider,
    }

    const formattedVideos = videos
      ?.filter(video => video?.live?.state !== 'scheduled')
      .map(video => ({
        postId: video.postId,
        provider: video.provider,
      }))

    VideoModalStore.setVideo({ currentVideo: formattedCurrentVideo, videos: formattedVideos, index }, state)

    onVideoClickCallback?.({ currentVideo })
  }

  const renderLoading = () => <Loading topMargin key="loader" />

  const renderVideoCount = (rows = 0, header) => {
    const label = rows > 1 ? 'Videos' : 'Video'

    if (header) {
      return header(rows)
    }

    return (
      <div>
        {titleTooltipText ? (
          <Title className="title" tag="h3">
            {!hideRowsInTitle ? rows.toLocaleString() : ' '} {label}
            <TooltipWrapper
              className="tooltip-wrapper"
              tooltipMessage={titleTooltipText}
              isTooltipMultiline
              tooltipStyle="tooltippedE"
            >
              <Icon icon="alert" fill="var(--color_warning_500)" width={16} height={16} />
            </TooltipWrapper>
          </Title>
        ) : (
          <Title tag="h3" className="title">
            {!hideRowsInTitle ? rows.toLocaleString() : ' '} {label}
          </Title>
        )}
        {showSortBy && <SortByFilterV2 pageName={pageName} options={sortByOptions} />}
        {subheader && (
          <Title weight="regular" tag="h6">
            {subheader}
          </Title>
        )}
      </div>
    )
  }

  const onSetGrid = () => {
    setState({ listView: 'grid' })
  }

  const onSetList = () => {
    setState({ listView: 'list' })
  }

  const renderFilters = () => {
    return (
      !hideListView && (
        <div className="filters-grid actions">
          <GridListSwitcher active="grid" onSetGrid={onSetGrid} onSetList={onSetList} />
          {customExportButtonComponent && <div className="export-button">{customExportButtonComponent}</div>}
        </div>
      )
    )
  }

  const { listView } = state
  const mustRenderTitle = videoCount !== undefined
  const containerClass = classNames('videos-grid-wrapper content', { 'title-actions-content': mustRenderTitle })
  const listViewClass = classNames(gridWrapperClass, {
    'videos-grid': listView === 'grid',
    'videos-list': listView === 'list',
  })

  const GridItemComponent = listView === 'grid' ? VideoCard : VideoCardList

  return (
    <div className={containerClass}>
      {mustRenderTitle && renderVideoCount(videoCount, header)}
      {renderFilters()}

      {!videos.length && loading ? (
        <div className="loading-wrapper content">
          <Loading topMargin showMessage={true} />
        </div>
      ) : (
        <div className="content videos-result">
          {shouldRenderMetaWarning && (
            <Alert
              message={t({
                key: 'VideosGrid-contentWarningMetaProvidersViews',
                defaultValue: "Facebook videos don't have views data available for now. Try sorting by likes.",
              })}
              variation="tip"
            />
          )}
          {shouldRenderTwitchWarning && (
            <Alert
              message={t({
                key: 'VideosGrid-contentWarningTwitchProvidersLikes',
                defaultValue: "Twitch videos don't have likes data available for now. Try sorting by views.",
              })}
              variation="tip"
            />
          )}
          {listView === 'list' && !!videos.length && (
            <div className="videos-list-header">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div>
                <T keyName="VideosGrid-listHeaderViews">Views</T>
              </div>
              <div>
                <T keyName="VideosGrid-listHeaderLikes">Likes</T>
              </div>
              <div>
                <T keyName="VideosGrid-listHeaderComments">Comments</T>
              </div>
              <div>
                <T keyName="VideosGrid-listHeaderUploadDate">Upload Date</T>
              </div>
            </div>
          )}

          <div className="infinite-wrapper">
            <div className={listViewClass}>
              {videos?.map((video, index) => {
                const removeVideo = topicId && isAdminUser ? e => onClickRemoveVideo(e, video) : null

                if (video?.creator) {
                  return (
                    <GridItemComponent
                      onCreatorClickCallback={onCreatorClickCallback}
                      isPublic={isPublic}
                      onClick={() => handleVideoClick(video, videos, index)}
                      onClickRemoveVideo={removeVideo}
                      key={video.postId}
                      index={index}
                      shouldEnableCreatorClick={shouldEnableCreatorClick}
                      hideCheckbox={hideCheckbox}
                      {...video}
                    />
                  )
                } else {
                  return null
                }
              })}
              {seeMoreAsCard && (
                <BlockComponents
                  tooltipPositionClassname="tooltipped-n"
                  isClickBlocked={isViewerUser && !shouldNotBlockJob}
                >
                  <SeeMoreCard className="video-grid-see-more-card" onClick={onSeeMoreCard} />
                </BlockComponents>
              )}
            </div>

            {(loading || hasMoreVideos) && <div ref={observerHookRef}>{renderLoading()}</div>}
          </div>
        </div>
      )}
      {!videos.length && !loading && (
        <div className="search-page-no-results content">
          <EmptyState headerText={customEmptyStateHeaderText} text={customEmptyStateText} />
        </div>
      )}
    </div>
  )
}

export default observer(VideosGrid)
